/**
 * KinsCircle Phase 6A + 6B — AI Feature Flag Layer
 *
 * STRICTLY ADDITIVE. ALL FLAGS DEFAULT TO FALSE.
 * Honors STABILITY.md §1, §3, §4 (rollback safety), §9, §10, §11, §12.
 *
 * When a flag is FALSE:
 *   - the corresponding helpers in `aiMemoryService.ts` early-return with
 *     a `{ ok: false, code: 'disabled' }` envelope (never throw)
 *   - the corresponding UI components render `null`
 *   - no edge-function calls are made
 *   - no behavior changes anywhere in the app
 *
 * Resolution order (first match wins) for every flag:
 *   1. window.__KINSCIRCLE_AI_*__                       (runtime override)
 *   2. localStorage 'kinscircle.ai.*.enabled' === '1'   (persistent override)
 *   3. import.meta.env.VITE_ENABLE_AI_*                 (build-time)
 *   4. false                                            (default)
 *
 * Phase 6B adds:
 *   - rolloutControl, usageLimits, qualityFeedback, safetyFilter, testSandbox flags
 *   - rollout MODE (disabled / owner_only / admin_only / paid_beta / all_premium)
 *   - canUseAIForRole() — single source of truth for "is this user in the rollout?"
 */

type FlagKey =
  | 'memoryWriter'
  | 'timelineSummary'
  | 'memoryTags'
  | 'onThisDay'
  | 'diagnostics'
  // Phase 6B additions:
  | 'rolloutControl'
  | 'usageLimits'
  | 'qualityFeedback'
  | 'safetyFilter'
  | 'testSandbox'
  // Phase 6D — Legacy Presentation Layer V1 (read-only, default OFF):
  | 'legacyTimeline'
  | 'storyPlayback'
  | 'relationshipJourney'
  | 'familyDocumentary'
  | 'memorySpotlight'
  | 'legacyViewer'
  // Phase 6D.2 — Legacy Preservation + Guided Story Collections (read-only,
  // default OFF). All depend on the master `legacyTimeline` flag from 6D.
  | 'legacyCollections'
  | 'legacyChapters'
  | 'generationHandoff'
  | 'memoryPlaylist'
  | 'legacyBookmarks'
  | 'legacyPreservationViewer'
  // Phase 7A — Production Hardening + Reliability Layer (default OFF).
  // Pure stabilization flags — none introduce new features, all gate
  // performance / resilience / diagnostics surfaces and fall back to
  // existing behavior when OFF.
  | 'perfMode'
  | 'virtualizedMemories'
  | 'offlineDrafts'
  | 'healthMonitoring'
  | 'safeMediaLoading'
  | 'routeRecovery'
  | 'uploadRecovery'
  | 'inviteDiagnostics'
  // Phase 7B — Scale + Multi-Family Optimization Layer (default OFF).
  // Pure scale / caching / lazy-load / mobile-resilience flags. All
  // additionally require the master `perfMode` umbrella so a partial
  // rollout cannot accidentally light up just one scale surface.
  | 'graphVirtualization'
  | 'timelineChunking'
  | 'adaptiveCaching'
  | 'bandwidthProtection'
  | 'sessionCleanup'
  | 'lazyHydration'
  | 'mobileBatteryMode'
  | 'deepTreeMode'
  // Phase 7C — Global Scale Rendering + Cross-Family Readiness Layer
  // (default OFF). Pure additive scaling / branch-streaming / render-budget
  // / cross-household viewport prep. All additionally require `perfMode`.
  // When OFF every helper / module returns safe defaults and existing
  // app behavior is byte-for-byte unchanged.
  | 'globalScaleMode'
  | 'branchStreaming'
  | 'progressiveGraphHydration'
  | 'crossHouseholdViewport'
  | 'renderBudgeting'
  | 'viewStatePreservation'
  | 'routeCacheIsolation'
  | 'globalScaleDiagnostics'
  // Phase 7D — Duplicate Prevention Guardrail (default OFF). Pre-save
  // duplicate scan that runs before any new family_graph_members insert
  // and shows a "Possible existing family member found" confirmation
  // modal. Pure additive: when OFF the existing add-member flow runs
  // unchanged. NEVER auto-merges, NEVER deletes, NEVER overwrites.
  | 'duplicateGuardrail'
  | 'duplicateGuardrailDiagnostics'
  // Phase 8 — Distributed Family Graph Integrity + Ownership Resolution
  // Layer (default OFF). Pure read-only validation + manual review
  // workflows. NEVER auto-rewrites relationships, NEVER auto-merges,
  // NEVER auto-deletes, NEVER silently reconciles. Every action requires
  // explicit user/admin confirmation. All sub-flags additionally require
  // the master `graphIntegrityMode` umbrella so a partial rollout can
  // never light up a single integrity surface in isolation.
  | 'graphIntegrityMode'
  | 'graphIntegrityEngine'
  | 'ownershipResolution'
  | 'relationshipLocks'
  | 'relationshipReviewWorkflow'
  | 'graphIntegrityDiagnostics';





type FlagSpec = {
  windowKey: string;
  lsKey: string;
  envKey: string;
};

const SPECS: Record<FlagKey, FlagSpec> = {
  memoryWriter: {
    windowKey: '__KINSCIRCLE_AI_MEMORY_WRITER__',
    lsKey: 'kinscircle.ai.memoryWriter.enabled',
    envKey: 'VITE_ENABLE_AI_MEMORY_WRITER',
  },
  timelineSummary: {
    windowKey: '__KINSCIRCLE_AI_TIMELINE_SUMMARY__',
    lsKey: 'kinscircle.ai.timelineSummary.enabled',
    envKey: 'VITE_ENABLE_AI_TIMELINE_SUMMARY',
  },
  memoryTags: {
    windowKey: '__KINSCIRCLE_AI_MEMORY_TAGS__',
    lsKey: 'kinscircle.ai.memoryTags.enabled',
    envKey: 'VITE_ENABLE_AI_MEMORY_TAGS',
  },
  onThisDay: {
    windowKey: '__KINSCIRCLE_AI_ON_THIS_DAY__',
    lsKey: 'kinscircle.ai.onThisDay.enabled',
    envKey: 'VITE_ENABLE_AI_ON_THIS_DAY',
  },
  diagnostics: {
    windowKey: '__KINSCIRCLE_AI_DIAGNOSTICS__',
    lsKey: 'kinscircle.ai.diagnostics.enabled',
    envKey: 'VITE_ENABLE_AI_DIAGNOSTICS',
  },
  rolloutControl: {
    windowKey: '__KINSCIRCLE_AI_ROLLOUT_CONTROL__',
    lsKey: 'kinscircle.ai.rolloutControl.enabled',
    envKey: 'VITE_ENABLE_AI_ROLLOUT_CONTROL',
  },
  usageLimits: {
    windowKey: '__KINSCIRCLE_AI_USAGE_LIMITS__',
    lsKey: 'kinscircle.ai.usageLimits.enabled',
    envKey: 'VITE_ENABLE_AI_USAGE_LIMITS',
  },
  qualityFeedback: {
    windowKey: '__KINSCIRCLE_AI_QUALITY_FEEDBACK__',
    lsKey: 'kinscircle.ai.qualityFeedback.enabled',
    envKey: 'VITE_ENABLE_AI_QUALITY_FEEDBACK',
  },
  safetyFilter: {
    windowKey: '__KINSCIRCLE_AI_SAFETY_FILTER__',
    lsKey: 'kinscircle.ai.safetyFilter.enabled',
    envKey: 'VITE_ENABLE_AI_SAFETY_FILTER',
  },
  testSandbox: {
    windowKey: '__KINSCIRCLE_AI_TEST_SANDBOX__',
    lsKey: 'kinscircle.ai.testSandbox.enabled',
    envKey: 'VITE_ENABLE_AI_TEST_SANDBOX',
  },
  // Phase 6D — Legacy Presentation Layer V1 (read-only).
  legacyTimeline: {
    windowKey: '__KINSCIRCLE_AI_LEGACY_TIMELINE__',
    lsKey: 'kinscircle.ai.legacyTimeline.enabled',
    envKey: 'VITE_ENABLE_AI_LEGACY_TIMELINE',
  },
  storyPlayback: {
    windowKey: '__KINSCIRCLE_AI_STORY_PLAYBACK__',
    lsKey: 'kinscircle.ai.storyPlayback.enabled',
    envKey: 'VITE_ENABLE_AI_STORY_PLAYBACK',
  },
  relationshipJourney: {
    windowKey: '__KINSCIRCLE_AI_RELATIONSHIP_JOURNEY__',
    lsKey: 'kinscircle.ai.relationshipJourney.enabled',
    envKey: 'VITE_ENABLE_AI_RELATIONSHIP_JOURNEY',
  },
  familyDocumentary: {
    windowKey: '__KINSCIRCLE_AI_FAMILY_DOCUMENTARY__',
    lsKey: 'kinscircle.ai.familyDocumentary.enabled',
    envKey: 'VITE_ENABLE_AI_FAMILY_DOCUMENTARY',
  },
  memorySpotlight: {
    windowKey: '__KINSCIRCLE_AI_MEMORY_SPOTLIGHT__',
    lsKey: 'kinscircle.ai.memorySpotlight.enabled',
    envKey: 'VITE_ENABLE_AI_MEMORY_SPOTLIGHT',
  },
  legacyViewer: {
    windowKey: '__KINSCIRCLE_AI_LEGACY_VIEWER__',
    lsKey: 'kinscircle.ai.legacyViewer.enabled',
    envKey: 'VITE_ENABLE_AI_LEGACY_VIEWER',
  },
  // Phase 6D.2 — Legacy Preservation + Guided Story Collections.
  legacyCollections: {
    windowKey: '__KINSCIRCLE_AI_LEGACY_COLLECTIONS__',
    lsKey: 'kinscircle.ai.legacyCollections.enabled',
    envKey: 'VITE_ENABLE_AI_LEGACY_COLLECTIONS',
  },
  legacyChapters: {
    windowKey: '__KINSCIRCLE_AI_LEGACY_CHAPTERS__',
    lsKey: 'kinscircle.ai.legacyChapters.enabled',
    envKey: 'VITE_ENABLE_AI_LEGACY_CHAPTERS',
  },
  generationHandoff: {
    windowKey: '__KINSCIRCLE_AI_GENERATION_HANDOFF__',
    lsKey: 'kinscircle.ai.generationHandoff.enabled',
    envKey: 'VITE_ENABLE_AI_GENERATION_HANDOFF',
  },
  memoryPlaylist: {
    windowKey: '__KINSCIRCLE_AI_MEMORY_PLAYLIST__',
    lsKey: 'kinscircle.ai.memoryPlaylist.enabled',
    envKey: 'VITE_ENABLE_AI_MEMORY_PLAYLIST',
  },
  legacyBookmarks: {
    windowKey: '__KINSCIRCLE_AI_LEGACY_BOOKMARKS__',
    lsKey: 'kinscircle.ai.legacyBookmarks.enabled',
    envKey: 'VITE_ENABLE_AI_LEGACY_BOOKMARKS',
  },
  legacyPreservationViewer: {
    windowKey: '__KINSCIRCLE_AI_LEGACY_PRESERVATION_VIEWER__',
    lsKey: 'kinscircle.ai.legacyPreservationViewer.enabled',
    envKey: 'VITE_ENABLE_AI_LEGACY_PRESERVATION_VIEWER',
  },
  // Phase 7A — Production Hardening + Reliability Layer.
  perfMode: {
    windowKey: '__KINSCIRCLE_PERF_MODE__',
    lsKey: 'kinscircle.perf.perfMode.enabled',
    envKey: 'VITE_ENABLE_PERF_MODE',
  },
  virtualizedMemories: {
    windowKey: '__KINSCIRCLE_PERF_VIRTUALIZED_MEMORIES__',
    lsKey: 'kinscircle.perf.virtualizedMemories.enabled',
    envKey: 'VITE_ENABLE_PERF_VIRTUALIZED_MEMORIES',
  },
  offlineDrafts: {
    windowKey: '__KINSCIRCLE_PERF_OFFLINE_DRAFTS__',
    lsKey: 'kinscircle.perf.offlineDrafts.enabled',
    envKey: 'VITE_ENABLE_PERF_OFFLINE_DRAFTS',
  },
  healthMonitoring: {
    windowKey: '__KINSCIRCLE_PERF_HEALTH_MONITORING__',
    lsKey: 'kinscircle.perf.healthMonitoring.enabled',
    envKey: 'VITE_ENABLE_PERF_HEALTH_MONITORING',
  },
  safeMediaLoading: {
    windowKey: '__KINSCIRCLE_PERF_SAFE_MEDIA_LOADING__',
    lsKey: 'kinscircle.perf.safeMediaLoading.enabled',
    envKey: 'VITE_ENABLE_PERF_SAFE_MEDIA_LOADING',
  },
  routeRecovery: {
    windowKey: '__KINSCIRCLE_PERF_ROUTE_RECOVERY__',
    lsKey: 'kinscircle.perf.routeRecovery.enabled',
    envKey: 'VITE_ENABLE_PERF_ROUTE_RECOVERY',
  },
  uploadRecovery: {
    windowKey: '__KINSCIRCLE_PERF_UPLOAD_RECOVERY__',
    lsKey: 'kinscircle.perf.uploadRecovery.enabled',
    envKey: 'VITE_ENABLE_PERF_UPLOAD_RECOVERY',
  },
  inviteDiagnostics: {
    windowKey: '__KINSCIRCLE_PERF_INVITE_DIAGNOSTICS__',
    lsKey: 'kinscircle.perf.inviteDiagnostics.enabled',
    envKey: 'VITE_ENABLE_PERF_INVITE_DIAGNOSTICS',
  },

  // Phase 7B — Scale + Multi-Family Optimization Layer.
  graphVirtualization: {
    windowKey: '__KINSCIRCLE_SCALE_GRAPH_VIRTUALIZATION__',
    lsKey: 'kinscircle.scale.graphVirtualization.enabled',
    envKey: 'VITE_ENABLE_SCALE_GRAPH_VIRTUALIZATION',
  },
  timelineChunking: {
    windowKey: '__KINSCIRCLE_SCALE_TIMELINE_CHUNKING__',
    lsKey: 'kinscircle.scale.timelineChunking.enabled',
    envKey: 'VITE_ENABLE_SCALE_TIMELINE_CHUNKING',
  },
  adaptiveCaching: {
    windowKey: '__KINSCIRCLE_SCALE_ADAPTIVE_CACHING__',
    lsKey: 'kinscircle.scale.adaptiveCaching.enabled',
    envKey: 'VITE_ENABLE_SCALE_ADAPTIVE_CACHING',
  },
  bandwidthProtection: {
    windowKey: '__KINSCIRCLE_SCALE_BANDWIDTH_PROTECTION__',
    lsKey: 'kinscircle.scale.bandwidthProtection.enabled',
    envKey: 'VITE_ENABLE_SCALE_BANDWIDTH_PROTECTION',
  },
  sessionCleanup: {
    windowKey: '__KINSCIRCLE_SCALE_SESSION_CLEANUP__',
    lsKey: 'kinscircle.scale.sessionCleanup.enabled',
    envKey: 'VITE_ENABLE_SCALE_SESSION_CLEANUP',
  },
  lazyHydration: {
    windowKey: '__KINSCIRCLE_SCALE_LAZY_HYDRATION__',
    lsKey: 'kinscircle.scale.lazyHydration.enabled',
    envKey: 'VITE_ENABLE_SCALE_LAZY_HYDRATION',
  },
  mobileBatteryMode: {
    windowKey: '__KINSCIRCLE_SCALE_MOBILE_BATTERY_MODE__',
    lsKey: 'kinscircle.scale.mobileBatteryMode.enabled',
    envKey: 'VITE_ENABLE_SCALE_MOBILE_BATTERY_MODE',
  },
  deepTreeMode: {
    windowKey: '__KINSCIRCLE_SCALE_DEEP_TREE_MODE__',
    lsKey: 'kinscircle.scale.deepTreeMode.enabled',
    envKey: 'VITE_ENABLE_SCALE_DEEP_TREE_MODE',
  },

  // Phase 7C — Global Scale Rendering + Cross-Family Readiness Layer.
  globalScaleMode: {
    windowKey: '__KINSCIRCLE_GLOBAL_SCALE_MODE__',
    lsKey: 'kinscircle.global.globalScaleMode.enabled',
    envKey: 'VITE_ENABLE_GLOBAL_SCALE_MODE',
  },
  branchStreaming: {
    windowKey: '__KINSCIRCLE_GLOBAL_BRANCH_STREAMING__',
    lsKey: 'kinscircle.global.branchStreaming.enabled',
    envKey: 'VITE_ENABLE_GLOBAL_BRANCH_STREAMING',
  },
  progressiveGraphHydration: {
    windowKey: '__KINSCIRCLE_GLOBAL_PROGRESSIVE_GRAPH_HYDRATION__',
    lsKey: 'kinscircle.global.progressiveGraphHydration.enabled',
    envKey: 'VITE_ENABLE_GLOBAL_PROGRESSIVE_GRAPH_HYDRATION',
  },
  crossHouseholdViewport: {
    windowKey: '__KINSCIRCLE_GLOBAL_CROSS_HOUSEHOLD_VIEWPORT__',
    lsKey: 'kinscircle.global.crossHouseholdViewport.enabled',
    envKey: 'VITE_ENABLE_GLOBAL_CROSS_HOUSEHOLD_VIEWPORT',
  },
  renderBudgeting: {
    windowKey: '__KINSCIRCLE_GLOBAL_RENDER_BUDGETING__',
    lsKey: 'kinscircle.global.renderBudgeting.enabled',
    envKey: 'VITE_ENABLE_GLOBAL_RENDER_BUDGETING',
  },
  viewStatePreservation: {
    windowKey: '__KINSCIRCLE_GLOBAL_VIEW_STATE_PRESERVATION__',
    lsKey: 'kinscircle.global.viewStatePreservation.enabled',
    envKey: 'VITE_ENABLE_GLOBAL_VIEW_STATE_PRESERVATION',
  },
  routeCacheIsolation: {
    windowKey: '__KINSCIRCLE_GLOBAL_ROUTE_CACHE_ISOLATION__',
    lsKey: 'kinscircle.global.routeCacheIsolation.enabled',
    envKey: 'VITE_ENABLE_GLOBAL_ROUTE_CACHE_ISOLATION',
  },
  globalScaleDiagnostics: {
    windowKey: '__KINSCIRCLE_GLOBAL_SCALE_DIAGNOSTICS__',
    lsKey: 'kinscircle.global.globalScaleDiagnostics.enabled',
    envKey: 'VITE_ENABLE_GLOBAL_SCALE_DIAGNOSTICS',
  },

  // Phase 7D — Duplicate Prevention Guardrail.
  duplicateGuardrail: {
    windowKey: '__KINSCIRCLE_DUPLICATE_GUARDRAIL__',
    lsKey: 'kinscircle.guardrail.duplicateGuardrail.enabled',
    envKey: 'VITE_ENABLE_DUPLICATE_GUARDRAIL',
  },
  duplicateGuardrailDiagnostics: {
    windowKey: '__KINSCIRCLE_DUPLICATE_GUARDRAIL_DIAGNOSTICS__',
    lsKey: 'kinscircle.guardrail.duplicateGuardrailDiagnostics.enabled',
    envKey: 'VITE_ENABLE_DUPLICATE_GUARDRAIL_DIAGNOSTICS',
  },

  // Phase 8 — Distributed Family Graph Integrity + Ownership Resolution.
  graphIntegrityMode: {
    windowKey: '__KINSCIRCLE_INTEGRITY_MODE__',
    lsKey: 'kinscircle.integrity.graphIntegrityMode.enabled',
    envKey: 'VITE_ENABLE_INTEGRITY_MODE',
  },
  graphIntegrityEngine: {
    windowKey: '__KINSCIRCLE_INTEGRITY_ENGINE__',
    lsKey: 'kinscircle.integrity.graphIntegrityEngine.enabled',
    envKey: 'VITE_ENABLE_INTEGRITY_ENGINE',
  },
  ownershipResolution: {
    windowKey: '__KINSCIRCLE_INTEGRITY_OWNERSHIP_RESOLUTION__',
    lsKey: 'kinscircle.integrity.ownershipResolution.enabled',
    envKey: 'VITE_ENABLE_INTEGRITY_OWNERSHIP_RESOLUTION',
  },
  relationshipLocks: {
    windowKey: '__KINSCIRCLE_INTEGRITY_RELATIONSHIP_LOCKS__',
    lsKey: 'kinscircle.integrity.relationshipLocks.enabled',
    envKey: 'VITE_ENABLE_INTEGRITY_RELATIONSHIP_LOCKS',
  },
  relationshipReviewWorkflow: {
    windowKey: '__KINSCIRCLE_INTEGRITY_REVIEW_WORKFLOW__',
    lsKey: 'kinscircle.integrity.relationshipReviewWorkflow.enabled',
    envKey: 'VITE_ENABLE_INTEGRITY_REVIEW_WORKFLOW',
  },
  graphIntegrityDiagnostics: {
    windowKey: '__KINSCIRCLE_INTEGRITY_DIAGNOSTICS__',
    lsKey: 'kinscircle.integrity.graphIntegrityDiagnostics.enabled',
    envKey: 'VITE_ENABLE_INTEGRITY_DIAGNOSTICS',
  },
};





const readWindow = (key: string): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return !!(window as any)[key];
  } catch {
    return false;
  }
};

const readLocal = (key: string): boolean => {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};

const readEnv = (key: string): boolean => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const env = (import.meta as any)?.env ?? {};
    const v = env[key];
    if (typeof v === 'string') return v === 'true' || v === '1';
    if (typeof v === 'boolean') return v;
  } catch {
    /* non-fatal */
  }
  return false;
};

const flagEnabled = (k: FlagKey): boolean => {
  const s = SPECS[k];
  if (readWindow(s.windowKey)) return true;
  if (readLocal(s.lsKey)) return true;
  if (readEnv(s.envKey)) return true;
  return false;
};

// ── Public flag API ─────────────────────────────────────────────────────────

export const isAIMemoryWriterEnabled = (): boolean => flagEnabled('memoryWriter');
export const isAITimelineEnabled = (): boolean => flagEnabled('timelineSummary');
export const isAIMemoryTagsEnabled = (): boolean => flagEnabled('memoryTags');
export const isAIOnThisDayEnabled = (): boolean => flagEnabled('onThisDay');

// Phase 6B
export const isAIRolloutControlEnabled = (): boolean => flagEnabled('rolloutControl');
export const isAIUsageLimitsEnabled = (): boolean => flagEnabled('usageLimits');
export const isAIQualityFeedbackEnabled = (): boolean => flagEnabled('qualityFeedback');
export const isAISafetyFilterEnabled = (): boolean => flagEnabled('safetyFilter');
export const isAITestSandboxEnabled = (): boolean => flagEnabled('testSandbox');

// Phase 6D — Legacy Presentation Layer V1.
// All read-only. Each helper requires the master `legacyTimeline` flag to
// avoid orphan UI when an admin only flips the dependent toggle.
export const isLegacyTimelineEnabled = (): boolean => flagEnabled('legacyTimeline');
export const isStoryPlaybackEnabled = (): boolean =>
  flagEnabled('legacyTimeline') && flagEnabled('storyPlayback');
export const isRelationshipJourneyEnabled = (): boolean =>
  flagEnabled('legacyTimeline') && flagEnabled('relationshipJourney');
export const isFamilyDocumentaryEnabled = (): boolean =>
  flagEnabled('legacyTimeline') && flagEnabled('familyDocumentary');
export const isMemorySpotlightEnabled = (): boolean =>
  flagEnabled('legacyTimeline') && flagEnabled('memorySpotlight');
export const isLegacyViewerEnabled = (): boolean =>
  flagEnabled('legacyTimeline') && flagEnabled('legacyViewer');

/** Snapshot of all Phase 6D flag states for the LegacyViewer admin page. */
export const snapshotLegacyFlags = (): Record<
  | 'legacyTimeline'
  | 'storyPlayback'
  | 'relationshipJourney'
  | 'familyDocumentary'
  | 'memorySpotlight'
  | 'legacyViewer',
  boolean
> => ({
  legacyTimeline: flagEnabled('legacyTimeline'),
  storyPlayback: flagEnabled('storyPlayback'),
  relationshipJourney: flagEnabled('relationshipJourney'),
  familyDocumentary: flagEnabled('familyDocumentary'),
  memorySpotlight: flagEnabled('memorySpotlight'),
  legacyViewer: flagEnabled('legacyViewer'),
});

// Phase 6D.2 — Legacy Preservation + Guided Story Collections.
// Each helper additionally requires the master `legacyTimeline` flag so a
// dependent toggle can never light up by itself when the umbrella system
// is off.
export const isLegacyCollectionsEnabled = (): boolean =>
  flagEnabled('legacyTimeline') && flagEnabled('legacyCollections');
export const isLegacyChaptersEnabled = (): boolean =>
  flagEnabled('legacyTimeline') && flagEnabled('legacyChapters');
export const isGenerationHandoffEnabled = (): boolean =>
  flagEnabled('legacyTimeline') && flagEnabled('generationHandoff');
export const isMemoryPlaylistEnabled = (): boolean =>
  flagEnabled('legacyTimeline') && flagEnabled('memoryPlaylist');
export const isLegacyBookmarksEnabled = (): boolean =>
  flagEnabled('legacyTimeline') && flagEnabled('legacyBookmarks');
export const isLegacyPreservationViewerEnabled = (): boolean =>
  flagEnabled('legacyTimeline') && flagEnabled('legacyPreservationViewer');

/** Snapshot of all Phase 6D.2 flag states for the LegacyPreservationViewer. */
export const snapshotLegacyPreservationFlags = (): Record<
  | 'legacyCollections'
  | 'legacyChapters'
  | 'generationHandoff'
  | 'memoryPlaylist'
  | 'legacyBookmarks'
  | 'legacyPreservationViewer',
  boolean
> => ({
  legacyCollections: flagEnabled('legacyCollections'),
  legacyChapters: flagEnabled('legacyChapters'),
  generationHandoff: flagEnabled('generationHandoff'),
  memoryPlaylist: flagEnabled('memoryPlaylist'),
  legacyBookmarks: flagEnabled('legacyBookmarks'),
  legacyPreservationViewer: flagEnabled('legacyPreservationViewer'),
});

// ── Phase 7A — Production Hardening + Reliability Layer ────────────────────
//
// Pure stabilization layer. None of these flags introduce new product
// features; they gate performance, resilience, and diagnostics surfaces.
// Every dependent helper / component MUST early-return when its flag is OFF
// so existing behavior is preserved unchanged. `perfMode` acts as a master
// umbrella — when OFF, all sub-flags resolve to `false` regardless of their
// individual state to prevent a partial rollout from accidentally lighting
// up just one resilience surface in production.

/** Master Phase 7A umbrella. Required for every sub-flag below. */
export const isPerfModeEnabled = (): boolean => flagEnabled('perfMode');

export const isVirtualizedMemoriesEnabled = (): boolean =>
  flagEnabled('perfMode') && flagEnabled('virtualizedMemories');
export const isOfflineDraftsEnabled = (): boolean =>
  flagEnabled('perfMode') && flagEnabled('offlineDrafts');
export const isHealthMonitoringEnabled = (): boolean =>
  flagEnabled('perfMode') && flagEnabled('healthMonitoring');
export const isSafeMediaLoadingEnabled = (): boolean =>
  flagEnabled('perfMode') && flagEnabled('safeMediaLoading');
export const isRouteRecoveryEnabled = (): boolean =>
  flagEnabled('perfMode') && flagEnabled('routeRecovery');
export const isUploadRecoveryEnabled = (): boolean =>
  flagEnabled('perfMode') && flagEnabled('uploadRecovery');
export const isInviteDiagnosticsEnabled = (): boolean =>
  flagEnabled('perfMode') && flagEnabled('inviteDiagnostics');

/** Snapshot of all Phase 7A flag states for the system health dashboard. */
export const snapshotProductionHardeningFlags = (): Record<
  | 'perfMode'
  | 'virtualizedMemories'
  | 'offlineDrafts'
  | 'healthMonitoring'
  | 'safeMediaLoading'
  | 'routeRecovery'
  | 'uploadRecovery'
  | 'inviteDiagnostics',
  boolean
> => ({
  perfMode: flagEnabled('perfMode'),
  virtualizedMemories: flagEnabled('virtualizedMemories'),
  offlineDrafts: flagEnabled('offlineDrafts'),
  healthMonitoring: flagEnabled('healthMonitoring'),
  safeMediaLoading: flagEnabled('safeMediaLoading'),
  routeRecovery: flagEnabled('routeRecovery'),
  uploadRecovery: flagEnabled('uploadRecovery'),
  inviteDiagnostics: flagEnabled('inviteDiagnostics'),
});

// ── Phase 7B — Scale + Multi-Family Optimization Layer ─────────────────────
//
// Pure scale / caching / lazy-load / mobile-resilience layer. Like Phase 7A,
// every sub-flag additionally requires the `perfMode` master umbrella so a
// partial rollout cannot accidentally light up a single scale surface in
// production. Each helper / module MUST early-return when its flag is OFF
// so existing rendering, network, and storage behavior is preserved
// unchanged.

export const isGraphVirtualizationEnabled = (): boolean =>
  flagEnabled('perfMode') && flagEnabled('graphVirtualization');
export const isTimelineChunkingEnabled = (): boolean =>
  flagEnabled('perfMode') && flagEnabled('timelineChunking');
export const isAdaptiveCachingEnabled = (): boolean =>
  flagEnabled('perfMode') && flagEnabled('adaptiveCaching');
export const isBandwidthProtectionEnabled = (): boolean =>
  flagEnabled('perfMode') && flagEnabled('bandwidthProtection');
export const isSessionCleanupEnabled = (): boolean =>
  flagEnabled('perfMode') && flagEnabled('sessionCleanup');
export const isLazyHydrationEnabled = (): boolean =>
  flagEnabled('perfMode') && flagEnabled('lazyHydration');
export const isMobileBatteryModeEnabled = (): boolean =>
  flagEnabled('perfMode') && flagEnabled('mobileBatteryMode');
export const isDeepTreeModeEnabled = (): boolean =>
  flagEnabled('perfMode') && flagEnabled('deepTreeMode');

/** Snapshot of all Phase 7B flag states for the system health dashboard. */
export const snapshotScaleFlags = (): Record<
  | 'graphVirtualization'
  | 'timelineChunking'
  | 'adaptiveCaching'
  | 'bandwidthProtection'
  | 'sessionCleanup'
  | 'lazyHydration'
  | 'mobileBatteryMode'
  | 'deepTreeMode',
  boolean
> => ({
  graphVirtualization: flagEnabled('graphVirtualization'),
  timelineChunking: flagEnabled('timelineChunking'),
  adaptiveCaching: flagEnabled('adaptiveCaching'),
  bandwidthProtection: flagEnabled('bandwidthProtection'),
  sessionCleanup: flagEnabled('sessionCleanup'),
  lazyHydration: flagEnabled('lazyHydration'),
  mobileBatteryMode: flagEnabled('mobileBatteryMode'),
  deepTreeMode: flagEnabled('deepTreeMode'),
});

// ── Phase 7C — Global Scale Rendering + Cross-Family Readiness Layer ───────
//
// Pure additive scaling layer for very large multi-household graphs, deep
// branch navigation, and global-scale render pressure. Like Phase 7A/7B,
// every sub-flag additionally requires the `perfMode` master umbrella so a
// partial rollout cannot accidentally light up a single scale surface in
// production. Each helper / module MUST early-return when its flag is OFF
// so existing rendering, network, and storage behavior is preserved
// byte-for-byte unchanged. `globalScaleMode` acts as the Phase 7C umbrella
// in addition to `perfMode`.

/** Master Phase 7C umbrella. Required for every sub-flag below. */
export const isGlobalScaleModeEnabled = (): boolean =>
  flagEnabled('perfMode') && flagEnabled('globalScaleMode');

export const isBranchStreamingEnabled = (): boolean =>
  flagEnabled('perfMode') &&
  flagEnabled('globalScaleMode') &&
  flagEnabled('branchStreaming');
export const isProgressiveGraphHydrationEnabled = (): boolean =>
  flagEnabled('perfMode') &&
  flagEnabled('globalScaleMode') &&
  flagEnabled('progressiveGraphHydration');
export const isCrossHouseholdViewportEnabled = (): boolean =>
  flagEnabled('perfMode') &&
  flagEnabled('globalScaleMode') &&
  flagEnabled('crossHouseholdViewport');
export const isRenderBudgetingEnabled = (): boolean =>
  flagEnabled('perfMode') &&
  flagEnabled('globalScaleMode') &&
  flagEnabled('renderBudgeting');
export const isViewStatePreservationEnabled = (): boolean =>
  flagEnabled('perfMode') &&
  flagEnabled('globalScaleMode') &&
  flagEnabled('viewStatePreservation');
export const isRouteCacheIsolationEnabled = (): boolean =>
  flagEnabled('perfMode') &&
  flagEnabled('globalScaleMode') &&
  flagEnabled('routeCacheIsolation');
export const isGlobalScaleDiagnosticsEnabled = (): boolean =>
  flagEnabled('perfMode') &&
  flagEnabled('globalScaleMode') &&
  flagEnabled('globalScaleDiagnostics');

/** Snapshot of all Phase 7C flag states for the global-scale diagnostics tab. */
export const snapshotGlobalScaleFlags = (): Record<
  | 'globalScaleMode'
  | 'branchStreaming'
  | 'progressiveGraphHydration'
  | 'crossHouseholdViewport'
  | 'renderBudgeting'
  | 'viewStatePreservation'
  | 'routeCacheIsolation'
  | 'globalScaleDiagnostics',
  boolean
> => ({
  globalScaleMode: flagEnabled('globalScaleMode'),
  branchStreaming: flagEnabled('branchStreaming'),
  progressiveGraphHydration: flagEnabled('progressiveGraphHydration'),
  crossHouseholdViewport: flagEnabled('crossHouseholdViewport'),
  renderBudgeting: flagEnabled('renderBudgeting'),
  viewStatePreservation: flagEnabled('viewStatePreservation'),
  routeCacheIsolation: flagEnabled('routeCacheIsolation'),
  globalScaleDiagnostics: flagEnabled('globalScaleDiagnostics'),
});

// ── Phase 7D — Duplicate Prevention Guardrail ──────────────────────────────
//
// Pre-save scan that runs before any new family_graph_members insert and
// surfaces a "Possible existing family member found" confirmation modal.
// Pure additive: when OFF the existing add-member flow is byte-for-byte
// unchanged. NEVER auto-merges, NEVER deletes, NEVER overwrites.

export const isDuplicateGuardrailEnabled = (): boolean =>
  flagEnabled('duplicateGuardrail');
export const isDuplicateGuardrailDiagnosticsEnabled = (): boolean =>
  flagEnabled('duplicateGuardrail') &&
  flagEnabled('duplicateGuardrailDiagnostics');

/** Snapshot of all Phase 7D flag states for the duplicate-prevention diagnostics card. */
export const snapshotDuplicateGuardrailFlags = (): Record<
  | 'duplicateGuardrail'
  | 'duplicateGuardrailDiagnostics',
  boolean
> => ({
  duplicateGuardrail: flagEnabled('duplicateGuardrail'),
  duplicateGuardrailDiagnostics: flagEnabled('duplicateGuardrailDiagnostics'),
});

// ── Phase 8 — Distributed Family Graph Integrity + Ownership Resolution ────
//
// Pure read-only validation + manual review workflow layer for protecting
// multi-user family trees as thousands of relatives join, claim profiles,
// and edit relationships concurrently. NEVER auto-rewrites, NEVER auto-
// merges, NEVER auto-deletes, NEVER silently reconciles. Every action
// requires explicit user / owner / admin confirmation. `graphIntegrityMode`
// acts as the umbrella — every sub-flag additionally requires it so a
// partial rollout cannot light up a single integrity surface in isolation.
// When OFF every helper returns safe empty defaults and existing app
// behavior is byte-for-byte unchanged.

/** Master Phase 8 umbrella. Required for every sub-flag below. */
export const isGraphIntegrityModeEnabled = (): boolean =>
  flagEnabled('graphIntegrityMode');

export const isGraphIntegrityEngineEnabled = (): boolean =>
  flagEnabled('graphIntegrityMode') && flagEnabled('graphIntegrityEngine');
export const isOwnershipResolutionEnabled = (): boolean =>
  flagEnabled('graphIntegrityMode') && flagEnabled('ownershipResolution');
export const isRelationshipLocksEnabled = (): boolean =>
  flagEnabled('graphIntegrityMode') && flagEnabled('relationshipLocks');
export const isRelationshipReviewWorkflowEnabled = (): boolean =>
  flagEnabled('graphIntegrityMode') && flagEnabled('relationshipReviewWorkflow');
export const isGraphIntegrityDiagnosticsEnabled = (): boolean =>
  flagEnabled('graphIntegrityMode') && flagEnabled('graphIntegrityDiagnostics');

/** Snapshot of all Phase 8 flag states for the graph-integrity diagnostics tab. */
export const snapshotGraphIntegrityFlags = (): Record<
  | 'graphIntegrityMode'
  | 'graphIntegrityEngine'
  | 'ownershipResolution'
  | 'relationshipLocks'
  | 'relationshipReviewWorkflow'
  | 'graphIntegrityDiagnostics',
  boolean
> => ({
  graphIntegrityMode: flagEnabled('graphIntegrityMode'),
  graphIntegrityEngine: flagEnabled('graphIntegrityEngine'),
  ownershipResolution: flagEnabled('ownershipResolution'),
  relationshipLocks: flagEnabled('relationshipLocks'),
  relationshipReviewWorkflow: flagEnabled('relationshipReviewWorkflow'),
  graphIntegrityDiagnostics: flagEnabled('graphIntegrityDiagnostics'),
});





/**
 * Owner / admin-only diagnostics surface. Even if the master flag is on,
 * normal members must never see the AI diagnostics card.
 */
export const canRunAIDiagnostics = (
  role: 'owner' | 'admin' | 'member' | 'pending' | string | null | undefined,
): boolean => {
  if (!flagEnabled('diagnostics')) return false;
  return role === 'owner' || role === 'admin';
};

/** Convenience setter for QA / dev — flips the persistent override. */
export const setAIFlagLocal = (flag: FlagKey, enabled: boolean): void => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    const k = SPECS[flag].lsKey;
    if (enabled) window.localStorage.setItem(k, '1');
    else window.localStorage.removeItem(k);
  } catch {
    /* non-fatal */
  }
};

// ── Phase 6B Rollout Control ────────────────────────────────────────────────

/**
 * Rollout modes — owner/admin can set this from the AI Settings page.
 * STORED LOCALLY ONLY in this V1 (per STABILITY.md §12). To enforce
 * server-side rollout, the edge function must read its own role/plan map.
 */
export type AIRolloutMode =
  | 'disabled'
  | 'owner_only'
  | 'admin_only'
  | 'paid_beta'
  | 'all_premium';

const ROLLOUT_LS_KEY = 'kinscircle.ai.rolloutMode.v1';

export const getAIRolloutMode = (): AIRolloutMode => {
  if (typeof window === 'undefined') return 'disabled';
  try {
    const v = window.localStorage.getItem(ROLLOUT_LS_KEY);
    if (
      v === 'disabled' ||
      v === 'owner_only' ||
      v === 'admin_only' ||
      v === 'paid_beta' ||
      v === 'all_premium'
    ) {
      return v;
    }
  } catch {
    /* non-fatal */
  }
  return 'disabled';
};

export const setAIRolloutMode = (mode: AIRolloutMode): void => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(ROLLOUT_LS_KEY, mode);
  } catch {
    /* non-fatal */
  }
};

/**
 * Single source of truth: should THIS user see / be able to use AI?
 *
 * If rolloutControl flag is OFF, falls back to legacy behavior:
 *   any user with an enabled per-feature flag may use it.
 * If rolloutControl flag is ON, the rollout MODE gates access:
 *   - disabled       → nobody
 *   - owner_only     → only owners
 *   - admin_only     → owners + admins
 *   - paid_beta      → owners + admins + monthly_premium + annual_founding + annual_standard
 *   - all_premium    → owners + admins + any paid plan
 */
export const canUseAIForRole = (
  role: 'owner' | 'admin' | 'member' | string | null | undefined,
  plan: string | null | undefined,
): boolean => {
  // Legacy fallback: rollout control disabled → allow if any AI flag is on.
  if (!isAIRolloutControlEnabled()) {
    return (
      isAIMemoryWriterEnabled() ||
      isAITimelineEnabled() ||
      isAIMemoryTagsEnabled() ||
      isAIOnThisDayEnabled()
    );
  }

  const mode = getAIRolloutMode();
  if (mode === 'disabled') return false;

  const isOwner = role === 'owner';
  const isAdmin = role === 'admin';
  const isPaid =
    plan === 'monthly_premium' ||
    (plan === 'annual_founding' || plan === 'annual_standard') ||
    plan === 'legacy_plus' ||
    plan === 'ai_plan';

  if (mode === 'owner_only') return isOwner;
  if (mode === 'admin_only') return isOwner || isAdmin;
  if (mode === 'paid_beta') return isOwner || isAdmin || isPaid;
  if (mode === 'all_premium') return isOwner || isAdmin || isPaid;
  return false;
};

export type { FlagKey };
